import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import type { HTMLMotionProps } from 'motion/react';
import { useReducedMotion } from 'motion/react';
import { useMagnetic } from '@hooks/useMagnetic';
import {
  StyledButton,
  SpinnerIcon,
  ButtonContent,
  LoadingOverlay,
  type ButtonVariant,
  type ButtonSize,
} from './styles';

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  inverse?: boolean;
  magneticStrength?: number;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    inverse = false,
    magneticStrength = 0,
    iconLeft,
    iconRight,
    disabled,
    ...rest
  },
  _ref,
) {
  const prefersReducedMotion = useReducedMotion();
  const { ref: magneticRef, x, y, handlers } = useMagnetic<HTMLButtonElement>({
    strength: prefersReducedMotion ? 0 : magneticStrength,
  });

  return (
    <StyledButton
      ref={magneticRef}
      $variant={variant}
      $size={size}
      $inverse={inverse}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      aria-busy={loading}
      aria-disabled={disabled || loading}
      style={{ x, y }}
      whileTap={{ scale: 0.96 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      {...handlers}
      {...rest}
    >
      {loading && (
        <LoadingOverlay aria-hidden>
          <SpinnerIcon />
        </LoadingOverlay>
      )}
      <ButtonContent $loading={loading}>
        {iconLeft}
        {children}
        {iconRight}
      </ButtonContent>
    </StyledButton>
  );
});

Button.displayName = 'Button';
